import {
  add, differenceInMinutes, format, parseISO, isValid,
} from 'date-fns';
import momentTZ from 'moment-timezone';
import moment from 'moment';
import {ICellRendererParams, ValueFormatterParams, ValueGetterParams} from 'ag-grid-community';

/**
 * Converts a time string in 24-hour format (HH:mm) to 12-hour format with AM/PM.
 * @param {string} time - Time string in 'HH:mm' format.
 * @returns {string} Time in 12-hour format with AM/PM.
 */
export function format24to12(time: string) {
  const [hours, minutes] = time.split(':');
  const parsedHours = Number(hours);
  const parsedMinutes = Number(minutes);
  if (
    !Number.isInteger(parsedHours)
    || !Number.isInteger(parsedMinutes)
    || parsedHours < 0
    || parsedHours > 23
    || parsedMinutes < 0
    || parsedMinutes > 59
  ) {
    return '';
  }

  const today = new Date();
  today.setHours(parsedHours);
  today.setMinutes(parsedMinutes);
  return format(today, 'p');
}

/**
 * Converts a date string in YYYYMMDD format to a human-friendly date string.
 * @param {string} date - Date string in 'YYYYMMDD' format.
 * @returns {string} Human-friendly date string.
 * @throws {Error} If the input date is invalid.
 */
export function formatYYYYMMDDToHumanFriendlyDate(date: string) {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(4, 6)) - 1;
  const day = Number(date.slice(6, 8));
  const dateObj: Date = new Date(year, month, day);
  if (isNaN(dateObj.getTime())) {
    throw new TypeError(`Given date is invalid for YYYYMMDD format: ${date}`);
  }

  return format(dateObj, 'LLL dd y');
}

/**
 * Formats a Date object to a human-friendly string.
 * @param {Date} date - The date to format.
 * @returns {string} Formatted date string.
 */
export function formatDate(date: Date) {
  if (!isValid(date)) {
    return '';
  }

  return format(date, 'LLL dd y');
}

/**
 * Converts a duration in seconds to a human-readable string (HH:mm:ss or mm:ss).
 * @param {number} durationInSeconds - Duration in seconds.
 * @param {boolean} [hoursInMinutes] - If true, converts hours to minutes.
 * @returns {string} Human-readable duration string.
 */
export function humanizeDuration(durationInSeconds: number, hoursInMinutes?: boolean) {
  function zeroPad(number: number) {
    return String(number).padStart(2, '0');
  }

  let hours: number | undefined = Math.floor(durationInSeconds / 3600);
  let minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  if (hoursInMinutes && hours >= 1) {
    minutes += (hours * 60);
    hours = undefined;
  }

  if (durationInSeconds <= 0) {
    return '00:00';
  }

  if (durationInSeconds <= 3600) {
    return `${zeroPad(minutes)}:${zeroPad(seconds)}`;
  }

  if (durationInSeconds <= 60) {
    return `00:${zeroPad(seconds)}`;
  }

  return `${hours ? `${zeroPad(hours)}:` : ''}${zeroPad(minutes)}:${zeroPad(seconds)}`;
}

/**
 * Calculates the number of days between two dates.
 * @param {Date} start - The start date.
 * @param {Date} target - The target date.
 * @returns {number} Number of days until the target date.
 */
export function daysUntil(start: Date, target: Date): number {
  const timeBetween = target.getTime() - start.getTime();
  return Math.ceil(timeBetween / (1000 * 60 * 60 * 24));
}

/**
 * Formats a Date object to a long date string (e.g., "January 1, 2023").
 * @param {Date} date - The date to format.
 * @returns {string} Formatted long date string.
 */
export function formatDateLong(date: Date): string {
  return date.toLocaleDateString('en-us', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Returns a Date object representing today with the time set to midnight.
 * @returns {Date} Today's date at midnight.
 */
export function today(): Date {
  return new Date(new Date().setHours(0, 0, 0, 0));
}

/**
 * Converts a FIX tag date in yyyymmdd format to yyyy-mm-dd, or parses an ISO date string to yyyy-mm-dd.
 * @param val FIX Tag Date in yyyymmdd format or ISO date string.
 * @returns string in yyyy-mm-dd format, or empty string if invalid.
 */
export function convertFromFixDateFormat(val: string): string {
  if (!val || typeof val !== 'string') {
    return '';
  }

  // Try to parse as ISO date string
  const isoDate = parseISO(val);
  if (isValid(isoDate)) {
    const year = isoDate.getFullYear();
    const month = isoDate.getMonth() + 1;
    const day = isoDate.getDate();
    return [
      year.toString(),
      month.toString().padStart(2, '0'),
      day.toString().padStart(2, '0'),
    ].join('-');
  }

  // Fallback to yyyymmdd format
  if (val.length !== 8) {
    return '';
  }

  if (!/^\d{8}$/.test(val)) {
    return '';
  }

  const year = Number.parseInt(val.slice(0, 4), 10);
  const month = Number.parseInt(val.slice(4, 6), 10);
  const day = Number.parseInt(val.slice(6, 8), 10);
  // Basic range checks
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return '';
  }

  // Validate date by creating a Date object
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return '';
  }

  return [
    year.toString(),
    month.toString().padStart(2, '0'),
    day.toString().padStart(2, '0'),
  ].join('-');
}

/**
 * Determines if the datestring value is valid for use in native input type="date" elements
 * @param dateString yyyy-mm-dd format
 * @returns boolean
 */
export const isValidDatepickerValue = (dateString: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) {
    return false;
  }

  const d = new Date(dateString);
  const dTime = d.getTime();
  if (!dTime && dTime !== 0) {
    return false;
  }

  return d.toISOString().slice(0, 10) === dateString;
};

/**
 * Returns a date string for tomorrow in yyyy-mm-dd format.
 * @returns {string} Tomorrow's date string.
 */
export const getTomorrowDateString = (): string => {
  const d = new Date();
  const tomorrow = new Date(d);
  tomorrow.setDate(d.getDate() + 1);

  return [
    tomorrow.getFullYear(),
    (tomorrow.getMonth() + 1).toString().padStart(2, '0'),
    tomorrow.getDate().toString().padStart(2, '0'),
  ].join('-');
};

/**
 * Returns a date string for today in yyyy-mm-dd format.
 * @returns {string} Today's date string.
 */
export function todayYYYYMMDD() {
  const d = new Date();
  return [
    d.getFullYear(),
    (d.getMonth() + 1).toString().padStart(2, '0'),
    d.getDate().toString().padStart(2, '0'),
  ].join('-');
}

/**
 * Converts a date to a moment object in UTC, trying multiple formats.
 * @param {any} date - The date to convert.
 * @returns {momentTZ.Moment|null} Moment object in UTC or null if invalid.
 */
export const getUTCDate = date => {
  let currentDate;
  if (typeof date === 'string') {
    currentDate = momentTZ.utc(date, 'YYYYMMDD-HH:mm:ss.SSSSSS', true);
    if (!currentDate.isValid()) {
      currentDate = momentTZ.utc(date, momentTZ.ISO_8601, true);
    }
  } else {
    currentDate = momentTZ.utc(date);
  }

  if (!currentDate.isValid()) {
    return null;
  }

  return currentDate;
};

/**
 * Ag-grid column formatter
 * Formats a date field value for display in AG Grid.
 * @param {ValueFormatterParams} params - AG Grid cell params.
 * @param {string} field - The field to format.
 * @returns {string|undefined} Formatted date string or undefined.
 */
export const dateFieldValueFormatter = (params: ValueFormatterParams, field: string) => {
  if (!params.value) {
    return;
  }

  return params.value.format('MM/DD/YYYY HH:mm:ss');
};

/**
 * Ag-grid column formatter
 * Gets a moment date value from the first available field in the provided list.
 * @param {ValueGetterParams} value - AG Grid cell value.
 * @param {string[]} fields - List of field names to check.
 * @returns {momentTZ.Moment|string|undefined} Moment object, empty string, or undefined.
 */
export const dateFieldValueGetter = (value: ValueGetterParams, fields: string[]) => {
  if (!value.data) {
    return;
  }

  let currentDate: momentTZ.Moment | string | null = null;
  for (let i = 0; i < fields.length; i++) {
    const field: string = fields[i];
    if (value.data.hasOwnProperty(field) && value.data[field]) {
      currentDate = getUTCDate(value.data[field]);
      break;
    }
  }

  if (!currentDate) {
    return '';
  }

  return currentDate;
};

/**
 * Converts a moment date to local time.
 * @param {momentTZ.Moment} date - The moment date to localize.
 * @returns {momentTZ.Moment} Localized moment date.
 */
export const localizeDate = (date: momentTZ.Moment): momentTZ.Moment => date?.clone()?.local();

/**
 * Converts a moment date to a JavaScript Date object at the start of the day.
 * @param {momentTZ.Moment} date - The moment date.
 * @returns {Date} JavaScript Date at start of day.
 */
export const getDateForFiltering = (date: momentTZ.Moment): Date => date?.clone()?.startOf('day').toDate();

/**
 * Formats a moment date as MM/DD/YYYY with no timestamp.
 * @param {momentTZ.Moment} date - The moment date.
 * @returns {string} Formatted date string.
 */
export const getDateNoTimestamp = (date: momentTZ.Moment): string => date?.startOf('day').format('MM/DD/YYYY');

/**
 * Formats a moment date as HH:mm:ss (time only).
 * @param {momentTZ.Moment} date - The moment date.
 * @returns {string} Time string.
 */
export const getDateTimestampOnly = (date: momentTZ.Moment): string => date?.format('HH:mm:ss');

/**
 * Ag-grid column formatter
 * Formats an expiration date for display.
 * @param {ICellRendererParams} [params] - AG Grid cell params.
 * @returns {string} Formatted locale expiration date string.
 */
export const expirationDateFormatter = (params: ICellRendererParams): string => {
  const expDate = params.data?.expirationDate;
  if (!expDate) {
    return '';
  }

  let date: Date | string;

  if (/^\d{8}$/.test(expDate)) {
    const year = expDate.slice(0, 4);
    const month = expDate.slice(4, 6);
    const day = expDate.slice(6, 8);
    date = new Date(`${year}-${month}-${day}T00:00:00`);
  } else {
    const date2 = new Date(expDate);
    if (isNaN(date2.getTime())) {
      console.error(`Invalid date input: ${expDate}`);
      return '';
    }

    date = new Date(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate());
  }

  return date.toLocaleDateString();
};

/** Describes a wall-clock time that repeats every day in an IANA timezone. */
export type DailyTimeSchedule = {
  timeZone: string;
  hour: number;
  minute: number;
  second?: number;
  millisecond?: number;
};

/**
 * Converts the legacy Date input into a recurring schedule in the workstation timezone.
 * @param time Date whose local wall-clock fields define the recurring time.
 * @returns Daily schedule that preserves the Date input's legacy local-time behavior.
 */
const dailyScheduleFromDate = (time: Date): DailyTimeSchedule => {
  const timeZone = momentTZ.tz.guess();
  const zonedTime = momentTZ(time).tz(timeZone);
  return {
    timeZone,
    hour: zonedTime.hour(),
    minute: zonedTime.minute(),
    second: zonedTime.second(),
    millisecond: zonedTime.millisecond(),
  };
};

/**
 * Calculates the next strictly-future occurrence so DST and multi-day sleep gaps do not drift or replay missed days.
 * @param schedule Wall-clock fields and timezone for the recurring callback.
 * @param now Instant from which the next occurrence is calculated.
 * @returns Next occurrence as an absolute Date.
 */
const getNextDailyOccurrence = (schedule: DailyTimeSchedule, now: Date): Date => {
  const zonedNow = momentTZ(now).tz(schedule.timeZone);
  const next = zonedNow.clone().set({
    hour: schedule.hour,
    minute: schedule.minute,
    second: schedule.second ?? 0,
    millisecond: schedule.millisecond ?? 0,
  });

  if (!next.isAfter(zonedNow)) {
    next.add(1, 'day');
  }

  return next.toDate();
};

/**
 * Calls a function once at each daily wall-clock occurrence, checking every minute.
 * A late tick, including one after workstation sleep, invokes the callback once and advances directly to the next future boundary.
 * @param onTime Function invoked when the configured daily occurrence is reached.
 * @param time Explicit zoned schedule, or a legacy Date whose local wall-clock fields repeat daily.
 * @returns Function that stops the polling interval.
 */
export function callAtTime(onTime: () => void, time: Date | DailyTimeSchedule): () => void {
  const intervalMilliseconds = 60_000;
  const isLegacyDateSchedule = time instanceof Date;
  const schedule = isLegacyDateSchedule ? dailyScheduleFromDate(time) : time;
  let isWaitingForLegacyInitialRun = isLegacyDateSchedule;
  let nextRun = isLegacyDateSchedule ? time : getNextDailyOccurrence(schedule, new Date());

  const interval = setInterval(() => {
    const now = new Date();
    // Preserve the legacy test/runtime override only for a Date caller's initial deadline.
    const legacyOverride = isWaitingForLegacyInitialRun
      ? (globalThis as typeof globalThis & {timeToUse?: Date}).timeToUse
      : undefined;
    const deadline = legacyOverride ?? nextRun;
    if (now.getTime() < deadline.getTime()) {
      return;
    }

    isWaitingForLegacyInitialRun = false;
    nextRun = getNextDailyOccurrence(schedule, now);
    onTime();
  }, intervalMilliseconds);

  return () => {
    clearInterval(interval);
  };
}

/**
 *
 * @param {Func} onAwake Function to call when awake
 * @returns {Func} Function to stop the interval
 */
export const checkAwake = (onAwake): Function => {
  const INTERVAL = 250; const INTERVAL_LONG = 5000; const INTERVAL_SHORT = 450;
  let lastTime = Date.now();
  let asleep = false;

  const interval = setInterval(() => {
    const currentTime = Date.now();

    if (!asleep && currentTime > (lastTime + INTERVAL_LONG)) {
      asleep = true;
    }

    if (asleep && currentTime < (lastTime + INTERVAL_SHORT)) {
      asleep = false;
      onAwake();
    }

    lastTime = currentTime;
  }, INTERVAL);

  return () => {
    clearInterval(interval);
  };
};

/**
 * Ag-grid column formatter
 * Helper for AG Grid valueFormatter signature. Formats a date value for display.
 * @param {ValueFormatterParams} params - AG Grid cell params.
 * @returns {string} Formatted date string or empty string.
 */
export const dateFieldValueFormatterAgGrid = (params: ValueFormatterParams) => {
  if (!params.value) {
    return '';
  }

  if (typeof params.value.format === 'function') {
    return params.value.format('MM/DD/YYYY HH:mm:ss');
  }

  return params.value;
};

/**
 * Attempts to parse a date string into a moment object using several formats.
 * @param {string} dateString - The date string to parse.
 * @returns {moment.Moment} A moment object representing the parsed date.
 */
export const fixDate = (dateString: string): moment.Moment => {
  const validDBDateFormat = 'YYYY-MM-DD HH:mm:ss.SSSSSS';
  const invalidSimulatorDateFormat = 'YYYYMMDD-HH:mm:ss.SSSSSS';
  const isoDate = moment.utc(dateString, moment.ISO_8601, true);
  if (isoDate.isValid()) {
    return isoDate;
  }

  const simulatorDate = moment.utc(dateString, invalidSimulatorDateFormat, true);
  if (simulatorDate.isValid()) {
    return simulatorDate;
  }

  return moment.utc(dateString, validDBDateFormat, true);
};

/**
 * Converts a date string to an ISO string with timezone offset at midnight.
 * @param {string} date - The date string in yyyy-mm-dd format.
 * @returns {string} ISO string with timezone offset.
 */
export function toIsoString(date: string): string {
  const tzo = new Date(date).getTimezoneOffset();
  const dif = tzo >= 0 ? '-' : '+';
  const tzoHours = Math.floor(Math.abs(tzo) / 60).toString().padStart(2, '0');
  const tzoMinutes = (Math.abs(tzo) % 60).toString().padStart(2, '0');
  return `${date}T00:00:00.000${dif}${tzoHours}:${tzoMinutes}`;
}

/**
 * Removes all dashes from a date string.
 * @param {string} date - The date string in yyyy-mm-dd format.
 * @returns {string} The date string without dashes.
 */
export function toSimpleDate(date: string): string {
  return date.replaceAll('-', '');
}

/**
 * Ag-grid column formatter
 * @param {ValueFormatterParams<string>} parameters - AG Grid value formatter parameters.
 * @returns {string} Formatted date string or '-' if invalid.
 */
export function dateFormatter(parameters: ValueFormatterParams<string>) {
  const expirationIso = toIsoString(parameters.value);
  const date = new Date(expirationIso);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return formatDate(new Date(expirationIso));
}

/**
 * Converts a UTC date to a local JavaScript Date object.
 * @param {Date} dateString - The UTC date to convert.
 * @returns {Date} The local date.
 */
export const convertUTCToLocal = (dateString: Date) => moment.utc(dateString).local().toDate();

/**
 * Calculates the percentage of time elapsed between two dates, given a current time.
 * @param {Date} startTime - The start time.
 * @param {Date} endTime - The end time.
 * @param {Date} currentTime - The current time.
 * @returns {number} The percentage of time elapsed.
 */
export const calculateTimeDiffPercent = (startTime: Date, endTime: Date, currentTime: Date) => {
  const t1 = new Date(startTime);
  const t2 = new Date(endTime);
  let t3 = currentTime;
  if (t3 > t2) {
    t3 = t2;
  }

  const difTotal = (t2.getTime() - t1.getTime()) / 1000;
  const diffCurrent = (t3.getTime() - t1.getTime()) / 1000;
  return (diffCurrent / difTotal) * 100;
};

/**
 * Returns the number of minutes from the current time to a specified time string.
 * If the specified time is earlier than the current time, it calculates the minutes until that time on the next day.
 * @param {string} newTime - The time string in HH:mm format.
 * @returns {number|null} The number of minutes until the specified time, or null if invalid.
 */
export const getMinsFromTime = (newTime: string) => {
  const [hourStr, minStr] = newTime.split(':');
  const newDate = new Date();
  newDate.setHours(Number.parseInt(hourStr), Number.parseInt(minStr), 0, 0);

  const nowDate: Date = new Date();
  let targetDate = newDate;
  if (targetDate <= nowDate) {
    targetDate = add(targetDate, {days: 1});
  }

  return differenceInMinutes(targetDate, nowDate);
};

/**
 * Converts a number of minutes into an object with hours and minutes.
 * @param {number} mins - The total number of minutes.
 * @returns {{hours: number, minutes: number}} An object with hours and minutes.
 */
export const turnMinutesIntoHoursAndMinutes = (mins: number) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return {hours, minutes};
};

/**
 * Converts a number of minutes to a string in HH:MM format.
 * @param {number} minutes - The total number of minutes.
 * @returns {string} The time in HH:MM format.
 */
export const numberMinutesToHHMM = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins}`;
};
